import { toast } from 'sonner'
import { FEATURE_KEYS } from '@/generated/featureKeys.generated'

/**
 * Product areas that can be hidden per person. Generated from the backend
 * registry (`backend/web/src/feature-access/feature-registry.ts`), which also
 * evaluates the PostHog flags and enforces them, so the UI hides exactly the
 * areas the API refuses.
 */
export { FEATURE_KEYS }

export type FeatureKey = (typeof FEATURE_KEYS)[number]

/**
 * Route prefixes and the areas a page needs; most need one, and the first match
 * wins, so more specific prefixes come first. Unlisted routes are always reachable.
 */
const ROUTE_FEATURES: ReadonlyArray<readonly [prefix: string, features: readonly FeatureKey[]]> = [
  ['/intelligent-board', ['boards']],
  ['/board/', ['boards']],
  ['/chat', ['chat']],
  ['/digital-coworker', ['digital_coworker']],
  ['/workflow', ['workflows']],
  // Match Entity lives under the catalog but runs tag-mapping actions.
  ['/data-catalog/match-entity', ['data_catalog', 'tag_mapping']],
  ['/data-catalog', ['data_catalog']],
  ['/graph-editor', ['graph_editor']],
  ['/pnid-set', ['graph_editor']],
  ['/tag-mapping', ['tag_mapping']],
  ['/data/graph', ['knowledge_graph']],
  ['/knowledge-graph', ['knowledge_graph']],
  ['/human-expert-knowledge', ['expert_knowledge']],
  ['/connectors', ['connectors']],
  ['/plc-parser', ['plc_parser']],
]

/** Every area a page needs; empty when the page is always reachable. */
export function featuresForPath(pathname: string): readonly FeatureKey[] {
  const match = ROUTE_FEATURES.find(([prefix]) =>
    prefix.endsWith('/')
      ? pathname.startsWith(prefix)
      : pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
  return match?.[1] ?? []
}

/** Whether every area a page needs is on (the same test the route gate uses). */
export function pathAllowed(pathname: string, allowed: (feature: FeatureKey) => boolean): boolean {
  return featuresForPath(pathname).every(allowed)
}

/**
 * Where a nav item should go, or null to hide it. An item with gated children
 * (Data) stays while any of them is allowed, and points at the first one when
 * its own page is off, so an allowed child area is never left without a way in.
 */
export function navTarget(
  item: { path: string; childPaths?: readonly string[] },
  allowed: (feature: FeatureKey) => boolean
): string | null {
  if (pathAllowed(item.path, allowed)) return item.path
  const child = (item.childPaths ?? []).find(
    (p) => featuresForPath(p).length > 0 && pathAllowed(p, allowed)
  )
  return child ?? null
}

/** The main area a page belongs to, or null. */
export function featureForPath(pathname: string): FeatureKey | null {
  return featuresForPath(pathname)[0] ?? null
}

/** Where to send someone whose current page is switched off. */
const LANDING_ORDER: ReadonlyArray<readonly [path: string, feature: FeatureKey | null]> = [
  ['/intelligent-board', 'boards'],
  ['/data-catalog', 'data_catalog'],
  ['/digital-coworker', 'digital_coworker'],
  ['/workflow', 'workflows'],
  ['/settings', null],
]

export function firstAllowedPath(allowed: (feature: FeatureKey) => boolean): string {
  const hit = LANDING_ORDER.find(([, feature]) => feature === null || allowed(feature))
  return hit?.[0] ?? '/settings'
}

/** The code (and message) the backend guard sends with its 403. */
export const FEATURE_NOT_ENABLED_CODE = 'FEATURE_NOT_ENABLED'
export const FEATURE_NOT_ENABLED_MESSAGE = 'This feature is not enabled for your account.'

const TOAST_DEBOUNCE_MS = 3000
let lastToastAt = 0

/** Fired when the API refuses a feature, so the app reloads this person's list. */
export const FEATURE_ACCESS_STALE_EVENT = 'feature-access:stale'

/** The API refused a feature: the list this browser holds is stale. */
export function notifyFeatureRefusedByApi(): void {
  window.dispatchEvent(new Event(FEATURE_ACCESS_STALE_EVENT))
  notifyFeatureBlocked()
}

/** One debounced toast when a switched-off feature is refused or redirected. */
export function notifyFeatureBlocked(): void {
  const now = Date.now()
  if (now - lastToastAt >= TOAST_DEBOUNCE_MS) {
    lastToastAt = now
    toast.error(FEATURE_NOT_ENABLED_MESSAGE)
  }
}
